import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShieldAlert, ShieldCheck } from 'lucide-react';

const fmt = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const when = (d) => (d < 0 ? `Expired ${-d} day${d === -1 ? '' : 's'} ago` : d === 0 ? 'Expires today' : `${d} day${d === 1 ? '' : 's'} left`);

export const ExpiringTrainingCard = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/organisations/compliance/expiring', { params: { days: 60 } }).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, [api]);
  if (!data) return null;
  const items = data.items || [];
  return (
    <Card className={items.length ? 'border-amber-200' : 'border-slate-200'} data-testid="expiring-training-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg flex items-center gap-2">
          {items.length ? <ShieldAlert className="h-5 w-5 text-amber-600" /> : <ShieldCheck className="h-5 w-5 text-green-600" />}
          Training expiry
        </CardTitle>
        <div className="flex gap-2">
          {data.expired > 0 && <Badge className="bg-red-100 text-red-700" data-testid="expired-count">{data.expired} expired</Badge>}
          {data.due_soon > 0 && <Badge className="bg-amber-100 text-amber-700" data-testid="due-soon-count">{data.due_soon} due in 60 days</Badge>}
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="expiring-training-empty">No staff training expires in the next 60 days. You'll be emailed 30 days and 7 days before anything expires.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.slice(0, 8).map((i) => (
              <li key={i.certificate_id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`expiring-item-${i.certificate_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{i.user_name} <span className="text-slate-400 font-normal">· {i.course_title}</span></p>
                  <p className="text-xs text-slate-500">Expiry {fmt(i.expires_at)}{i.is_mandatory && ' · Mandatory'}</p>
                </div>
                <Badge className={i.status === 'expired' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{when(i.days_left)}</Badge>
              </li>
            ))}
          </ul>
        )}
        {items.length > 8 && <p className="text-xs text-slate-500 mt-2">+{items.length - 8} more</p>}
      </CardContent>
    </Card>
  );
};
